// src/common/ui/DdsDrawCell.jsx
//
// Shared DDS Draw cell used by Associate Fee, Journal Entry, and Check
// tables. Reads everything from `row.breakdown` (computed authoritatively
// on the backend in apps/monthly_compensation/calculations.py), so the
// number displayed here is identical to the value posted to NetSuite,
// rendered in the PDF, and emailed to practitioners.
//
// No financial calculations are performed here.
import Button from "./Button";
import Tooltip from "./Tooltip";
import { formatMoney } from "@/utils/format";

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function TrendIcon({ direction }) {
  const config = {
    up: {
      className: "text-palette-emerald-badge",
      label: "Higher than previous month",
      path: "M12 19V5m0 0l-5 5m5-5l5 5",
    },
    down: {
      className: "text-palette-rose-badge",
      label: "Lower than previous month",
      path: "M12 5v14m0 0l-5-5m5 5l5-5",
    },
    flat: {
      className: "text-palette-slate-placeholder",
      label: "Same as previous month",
      path: "M5 12h14",
    },
  }[direction];

  if (!config) return null;

  return (
    <span className={config.className} title={config.label} aria-label={config.label}>
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.path} />
      </svg>
    </span>
  );
}

// Inner content of the DDS Draw tooltip (the shared <Tooltip> supplies the
// portal, positioning, dark box, and arrow).
function DdsDrawContent({ breakdown }) {
  const basisLabel = breakdown.basis_label || "";
  const draw = toNum(breakdown.dds_draw);

  // Per-day pay: render a minimal tooltip showing num × value = draw.
  if (basisLabel === "Per Day") {
    const pdNum = toNum(breakdown.per_day_number);
    const pdVal = toNum(breakdown.per_day_value);
    const pdAmt = toNum(breakdown.per_day_amount ?? breakdown.dds_draw);
    return (
      <>
        <span
          className={`block font-semibold text-sm mb-1.5 ${draw >= 0 ? "text-palette-green-onDark" : "text-palette-red-indicator"}`}
        >
          DDS Draw Calculation (Per Day)
        </span>
        {"Per Day Number:".padEnd(18)}
        {pdNum}
        {"\n"}
        {"Per Day Value:".padEnd(18)}
        {formatMoney(pdVal)}
        {"\n"}
        <span className="block border-t border-palette-gray-badge mt-1.5 pt-1.5">
          {`${pdNum} × ${formatMoney(pdVal)} = `}
          <span
            className={`font-bold text-sm ${draw >= 0 ? "text-palette-green-onDark" : "text-palette-red-indicator"}`}
          >
            {formatMoney(pdAmt)}
          </span>
        </span>
      </>
    );
  }

  const basis = toNum(breakdown.basis);
  const net = toNum(breakdown.net_after_deductions);
  const pct = toNum(breakdown.fee_percentage);
  const feeAmount = toNum(breakdown.fee_amount);
  const insurance = toNum(breakdown.insurance);
  const otherProfitTotal = toNum(breakdown.other_profit_total);
  const realOtherProfitTiers = Array.isArray(breakdown.other_profit) ? breakdown.other_profit : [];
  const otherDeductionTotal = toNum(breakdown.other_deduction_total);
  const otherDeductionTiers = Array.isArray(breakdown.other_deduction)
    ? breakdown.other_deduction
    : [];
  const tierBreakdownLines = Array.isArray(breakdown.tier_breakdown)
    ? breakdown.tier_breakdown
    : [];
  // The main block shows the ladder for a grid-tier doctor, or the profit
  // tiers for a standard one — they occupy the same place in the math.
  const otherProfitTiers =
    tierBreakdownLines.length > 0 ? tierBreakdownLines : realOtherProfitTiers;
  const isTierBreakdown =
    tierBreakdownLines.length > 0 || (pct === 0 && otherProfitTiers.length > 0);
  // A grid-tier doctor can ALSO earn real other profit on top of the ladder, so
  // that list gets its own block below rather than being displaced by the
  // ladder. Empty for a standard doctor, whose profit tiers are already above.
  const extraOtherProfitTiers = tierBreakdownLines.length > 0 ? realOtherProfitTiers : [];
  const extraOtherProfitTotal = extraOtherProfitTiers.reduce((sum, t) => sum + toNum(t?.profit), 0);

  const deductions = (Array.isArray(breakdown.deductions) ? breakdown.deductions : []).filter(
    (d) => toNum(d?.value) !== 0,
  );

  return (
    <>
      <span
        className={`block font-semibold text-sm mb-1.5 ${draw >= 0 ? "text-palette-green-onDark" : "text-palette-red-indicator"}`}
      >
        DDS Draw Calculation ({basisLabel})
      </span>
      {`${basisLabel}:`.padEnd(18)}
      {formatMoney(basis)}
      {"\n"}
      {deductions.map((d) => {
        // `deducted === false` => value is shown for transparency but
        // already baked into the basis upstream (e.g. when the monthly
        // record has `included_adjustment_deduction = true`). Render
        // it without the "−" so the math reads correctly.
        const isDisplayOnly = d?.deducted === false;
        return (
          <span key={d.label}>
            {`${isDisplayOnly ? "  " : "− "}${d.label}${isDisplayOnly ? " (already in basis)" : ""}:`.padEnd(
              18,
            )}
            {formatMoney(toNum(d.value))}
            {"\n"}
          </span>
        );
      })}
      <span className="block border-t border-palette-gray-badge mt-1.5 pt-1.5">
        {!isTierBreakdown && (
          <>
            {"Net: "}
            {formatMoney(net)}
            {"\n"}
            {"× "}
            {pct}
            {"% = "}
            {formatMoney(feeAmount)}
          </>
        )}
        {insurance !== 0 && (
          <>
            {"\n"}
            {"− Insurance:      "}
            {formatMoney(insurance)}
          </>
        )}
        {otherProfitTiers.length > 0 && (
          <>
            {"\n"}
            <span className="block text-palette-emerald-ring font-semibold">
              {isTierBreakdown ? "Tier Breakdown" : "Other Profit"}
            </span>
            {otherProfitTiers.map((t, i) => {
              const tIn = toNum(t?.input_value);
              const tProfit = toNum(t?.profit);
              const tPct = Number(t?.percent) || 0;
              // For grid-tier breakdowns (Fardeen / Dr. Patel etc.), a
              // negative profit means a subtraction line ("Less BRO
              // Pay"); render as "− label … → $abs". For regular
              // user-entered other_profit, keep the signed amount as-is
              // so a negative input_value reads as a negative profit.
              if (isTierBreakdown && tProfit < 0) {
                return (
                  <span key={`op-${i}`}>
                    {`− ${t?.label || "Tier"}${tPct ? ` (${tPct}%)` : ""}: `}
                    {formatMoney(Math.abs(tIn))}
                    {" → "}
                    {formatMoney(Math.abs(tProfit))}
                    {"\n"}
                  </span>
                );
              }
              return (
                <span key={`op-${i}`}>
                  {`+ ${t?.label || "Tier"}${tPct ? ` (${tPct}%)` : ""}: `}
                  {formatMoney(tIn)}
                  {" → "}
                  {formatMoney(tProfit)}
                  {"\n"}
                </span>
              );
            })}
            {!isTierBreakdown && (
              <>
                {`+ Total Other Profit: `}
                {formatMoney(otherProfitTotal)}
                {"\n"}
              </>
            )}
          </>
        )}
        {extraOtherProfitTiers.length > 0 && (
          <>
            {"\n"}
            <span className="block text-palette-emerald-ring font-semibold">Other Profit</span>
            {extraOtherProfitTiers.map((t, i) => {
              const tIn = toNum(t?.input_value);
              const tProfit = toNum(t?.profit);
              const tPct = Number(t?.percent) || 0;
              // A negative profit is a correction/clawback — render it as a
              // "Less:" line so the running total still reads correctly.
              const sign = tProfit < 0 ? "−" : "+";
              return (
                <span key={`op-extra-${i}`}>
                  {`${sign} ${t?.label || "Tier"}${tPct ? ` (${tPct}%)` : ""}: `}
                  {formatMoney(Math.abs(tIn))}
                  {" → "}
                  {formatMoney(Math.abs(tProfit))}
                  {"\n"}
                </span>
              );
            })}
            {extraOtherProfitTiers.length > 1 && (
              <>
                {`= Total Other Profit: `}
                {formatMoney(extraOtherProfitTotal)}
                {"\n"}
              </>
            )}
          </>
        )}
        {otherDeductionTiers.length > 0 && (
          <>
            {"\n"}
            <span className="block text-palette-rose-ring font-semibold">Other Deduction</span>
            {otherDeductionTiers.map((t, i) => {
              const tAmount = toNum(t?.amount ?? t?.profit);
              // How the figure was reached, shown before the arrow. A
              // fixed-amount row has no derivation — the amount IS what was
              // entered — so it prints on its own.
              let detail = "";
              if (t?.mode === "amount") {
                detail = "";
              } else if (t?.hour_rate !== undefined || t?.hours !== undefined) {
                const tRate = Number(t?.hour_rate) || 0;
                const tHours = Number(t?.hours) || 0;
                detail = `${tHours}h × ${formatMoney(tRate)}`;
              } else if (t?.percent !== undefined || t?.input_value !== undefined) {
                const tIn = toNum(t?.input_value);
                const tPct = Number(t?.percent) || 0;
                detail = `${formatMoney(tIn)}${tPct ? ` × ${tPct}%` : ""}`;
              }
              return (
                <span key={`od-${i}`}>
                  {`- ${t?.label || "Tier"}: ${detail ? `${detail} → ` : ""}`}
                  {formatMoney(tAmount)}
                  {"\n"}
                </span>
              );
            })}
            {`- Total Other Deduction: `}
            {formatMoney(otherDeductionTotal)}
            {"\n"}
          </>
        )}
        {"\n"}
        <span
          className={`font-bold text-sm ${draw >= 0 ? "text-palette-green-onDark" : "text-palette-red-indicator"}`}
        >
          {"DDS Draw: "}
          {formatMoney(draw)}
        </span>
      </span>
    </>
  );
}

/**
 * Shared DDS Draw cell.
 *
 * Required: `row.breakdown` (provided by the backend on every associate-fee
 * row). When absent, falls back to a 0 draw so the cell still renders.
 *
 * Optional:
 *   - trendDirection: "up" | "down" | "flat" — show the month-over-month arrow.
 *   - onCompareHistory(row): show a "Compare to Previous Months" link below.
 *   - compact: when true, omits the wrapping container (used by simpler tables
 *     like Check / Journal Entry where we don't show trend or history).
 *   - breakdown: explicit breakdown override (used by per-doctor tier
 *     calculators that bypass the standard backend formula).
 */
export default function DdsDrawCell({
  row,
  trendDirection,
  onCompareHistory,
  compact = false,
  breakdown: breakdownProp,
}) {
  const fromRow = row?.breakdown && typeof row.breakdown === "object" ? row.breakdown : null;
  const breakdown = breakdownProp && typeof breakdownProp === "object" ? breakdownProp : fromRow;
  const draw = toNum(breakdown?.dds_draw);

  const drawSpan = (
    <Tooltip
      disabled={!breakdown}
      contentClassName="whitespace-pre-line leading-relaxed"
      triggerClassName={`font-bold text-base ${breakdown ? "cursor-help" : ""} ${draw > 0 ? "text-palette-green-text" : "text-palette-red-badge"}`}
      content={breakdown ? <DdsDrawContent breakdown={breakdown} /> : null}
    >
      {formatMoney(draw)}
    </Tooltip>
  );

  if (compact) {
    return drawSpan;
  }

  return (
    <div className="flex min-w-[170px] flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        {drawSpan}
        <TrendIcon direction={trendDirection} />
      </div>
      {onCompareHistory && (
        <Button
          variant="link"
          size="xs"
          onClick={() => onCompareHistory(row)}
          className="!h-auto !p-0 text-[11px] font-medium text-palette-indigo-badge hover:text-palette-indigo-heading"
        >
          Compare to Previous Months
        </Button>
      )}
    </div>
  );
}
